
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Component, Prop, Mixins } from "vue-property-decorator";
import { VuetifyMixin, BAIconsMixin, StringsMixin } from "@/mixins";
import { CalendarEventParticipant } from "@/../types/interfaces";
import { CalendarEventsApi } from "@/api/CalendarApi";
import { TeamModel } from "@/models/team";
import { UserProfileModel } from "@/models/UserProfileModel";
import { Tardiness } from '@/../types/enums/tardiness';
import { CalendarEventParticipantResponse } from '@/../types/enums';
import { participantRsvpOn } from '@/helpers/availability';

@Component
export default class TeamAttendance extends Mixins(VuetifyMixin, BAIconsMixin, StringsMixin) {

	@Prop({ default: [] }) participants: CalendarEventParticipant[];
	@Prop({ default: () => [] }) staffParticipants: CalendarEventParticipant[];
	@Prop({ default: false }) loading: boolean;
	@Prop({ required: true }) team: TeamModel;
	@Prop({default: 650}) height: number;
	@Prop({ default: false }) readOnly: boolean;
	/** Staff-only: set by the parent from the team roster, not from the hostname alone. */
	@Prop({ default: null }) canEdit: boolean | null;
	@Prop({default: true}) editAttendance: boolean;
	@Prop({ default: null }) eventId: string | null;
	@Prop({ default: null }) calendarEventsApi: CalendarEventsApi | null;
	/**
	 * YYYY-MM-DD when this is one date of a repeating event, undefined for a one-off event.
	 * Availability is stored per date, so reads and writes both have to carry it.
	 */
	@Prop({ default: undefined }) occurrenceDate: string | undefined;
	showFinishAttendance: boolean = false;
	savingRsvpFor: string | null = null;
	savingAttendanceFor: string | null = null;
	savingFinish: boolean = false;
	errorMessage: string | null = null;

	Tardiness = Tardiness;

	/** Coaches and team staff may edit; everyone else sees the roster read-only. */
	get CanEdit(): boolean{
		const allowed = this.canEdit === null ? !this.readOnly : this.canEdit;
		return allowed && this.calendarEventsApi !== null && this.IsNotEmpty(this.eventId);
	}
	/** The attendance toggles only appear while attendance taking is open. */
	get CanEditAttendance(): boolean{
		return this.CanEdit && this.editAttendance;
	}
	/**
	 * A coach can answer for a player who told them in person, at any time - not only while
	 * attendance taking is open. Attendance itself stays behind the Edit/Finish toggle.
	 */
	get CanEditRsvp(): boolean{
		return this.CanEdit;
	}

	/** The answer that applies on the date being viewed, not the whole series. */
	RsvpOf(attendee: CalendarEventParticipant): CalendarEventParticipantResponse{
		return participantRsvpOn(attendee, this.occurrenceDate);
	}

	/** Absent is dropped on purpose: a no-show already reads off the RSVP column. */
	get AttendanceOptions(): { value: Tardiness; label: string; class: string; style: Record<string, string> }[]{
		return [
			{ value: Tardiness.OnTime, label: 'On Time', class: '', style: this.OnTimeButton },
			{ value: Tardiness.Late, label: 'Late', class: 'px-4', style: this.LateButton },
		];
	}

	/**
	 * Staff records a participant's attendance. Saved on the spot, rolled back if it fails.
	 * Clicking the active button again is ignored: there is no endpoint for clearing a
	 * recorded status, and a no-show already reads off the RSVP column.
	 */
	async setAttendance(attendee: CalendarEventParticipant, tardiness: Tardiness | null): Promise<void>{
		if(!tardiness || tardiness === attendee.tardiness) return;

		const previous = attendee.tardiness;
		attendee.tardiness = tardiness;
		this.savingAttendanceFor = attendee.id;
		this.errorMessage = null;
		try{
			const updated = await this.calendarEventsApi.setParticipantAttendance(this.eventId, attendee.id, tardiness);
			this.$emit('updated', updated);
			this.updatedParticipants();
		}catch(e){
			attendee.tardiness = previous;
			this.errorMessage = 'Could not save attendance. Please try again.';
			// eslint-disable-next-line no-console
			console.error('Failed to save attendance', e);
		}finally{
			this.savingAttendanceFor = null;
		}
	}

	get RsvpOptions(): { value: CalendarEventParticipantResponse; label: string; icon: string; style: Record<string, string> }[]{
		return [
			{ value: CalendarEventParticipantResponse.Yes, label: 'Going', icon: this.BAIcons.checkmark, style: this.OnTimeButton },
			{ value: CalendarEventParticipantResponse.Maybe, label: 'Maybe', icon: this.BAIcons.checkMaybe, style: this.LateButton },
			{ value: CalendarEventParticipantResponse.No, label: 'Not going', icon: this.BAIcons.close, style: this.AbsentButton },
		];
	}

	async setRsvp(attendee: CalendarEventParticipant, selected: CalendarEventParticipantResponse | undefined): Promise<void>{
		// v-btn-toggle hands us undefined when the active button is clicked again: that
		// means "unset my answer", which is the None response rather than a no-op.
		const rsvp = selected ?? CalendarEventParticipantResponse.None;
		const previous = this.RsvpOf(attendee);
		if(rsvp === previous) return;
		this.applyRsvpLocally(attendee, rsvp);
		this.savingRsvpFor = attendee.id;
		try{
			const updated = await this.calendarEventsApi.setParticipantRsvp(this.eventId, attendee.id, rsvp, undefined, this.occurrenceDate);
			this.$emit('updated', updated);
		}catch(e){
			this.applyRsvpLocally(attendee, previous);
			this.errorMessage = 'Could not save availability. Please try again.';
			// eslint-disable-next-line no-console
			console.error('Failed to save availability', e);
		}finally{
			this.savingRsvpFor = null;
		}
	}

	/**
	 * Show the new answer straight away, in the same place the server will store it: against
	 * this date for a repeating event, against the participant for a one-off.
	 */
	private applyRsvpLocally(attendee: CalendarEventParticipant, rsvp: CalendarEventParticipantResponse): void{
		if(!this.occurrenceDate){
			attendee.rsvp = rsvp;
			return;
		}
		if(!Array.isArray(attendee.occurrenceRsvps)) this.$set(attendee, 'occurrenceRsvps', []);
		const existing = attendee.occurrenceRsvps.find(entry => entry.date === this.occurrenceDate);
		if(existing) existing.rsvp = rsvp;
		else attendee.occurrenceRsvps.push({ date: this.occurrenceDate, rsvp, note: '', respondedAt: new Date() });
	}

	cancelShowAttendance():void{
		this.showFinishAttendance = false;
	}
	async confirmShowAttendance(): Promise<void>{
		this.showFinishAttendance = false;
		await this.saveAttendanceFinished(true);
	}

	updatedParticipants(): void{
		this.$emit('update:participants', [...this.participants, ...this.Staff])
	}

	/** Open (Edit) or close (Finish) attendance taking. Closing asks for confirmation first. */
	async attendanceFinish(startEditing: boolean): Promise<void>{
		if (!startEditing){
			this.showFinishAttendance = true;
			return;
		}
		await this.saveAttendanceFinished(false);
	}

	/** `finished === false` means attendance taking is open for editing. */
	async saveAttendanceFinished(finished: boolean): Promise<void>{
		if (!this.CanEdit) return;
		this.savingFinish = true;
		this.errorMessage = null;
		try{
			const updated = await this.calendarEventsApi.setAttendanceFinished(this.eventId, finished);
			this.$emit('updated', updated);
			this.$emit('update:attendanceProgress', !finished);
		}catch(e){
			this.errorMessage = 'Could not update attendance. Please try again.';
			// eslint-disable-next-line no-console
			console.error('Failed to update attendance state', e);
		}finally{
			this.savingFinish = false;
		}
	}


	get Players(): CalendarEventParticipant[] {
		return this.participants;
	}
	get Staff(): CalendarEventParticipant[] {
		return this.staffParticipants || [];
	}
	get HasAttendees(): boolean {
		return this.Players.length > 0 || this.Staff.length > 0;
	}
	get Sections(): { key: string; title: string; list: CalendarEventParticipant[] }[] {
		return [
			{ key: 'players', title: 'Players', list: this.Players },
			{ key: 'staff', title: 'Coaches & Staff', list: this.Staff },
		];
	}
	get ScrollableHeight(): {'overflow-y':string, 'max-height':string}{
		return {'overflow-y':'auto', 'max-height': `${this.height - 140}px`};
	}

	get NeutralButton():Record<string, string>{
		return {
			'background-color': 'white !important',
		}
	}

	get OnTimeButton():Record<string, string>{
		return {
			color: 'white',
			'background-color': 'green',
		}
	}

	get LateButton(): Record<string,string> {
		return{
			color: 'white',
			'background-color': '#CE6100',
		}
	}

	get AbsentButton():Record<string,string>{
		return{
			color: 'white',
			'background-color': '#C80404',
		}
	}
	get FormColumns(): {
		RSVP: Record<string, string>;
		Name: Record<string, string>;
		Status: Record<string, string>;
	} {
		// Below md the name takes a full row and the two toggle groups split the next one.
		// Squeezing all three side by side used to push the RSVP buttons under the Status
		// column on phones and tablets.
		return {
			RSVP: {
				cols: "6",
				md: "3",
			},
			Name: {
				cols: "12",
				md: "4",
			},
			Status:{
				cols: "6",
				md: "5",
			}

		};
		
	}

	participantToUserInfo(participant: CalendarEventParticipant): Partial<UserProfileModel> {
		if (participant.coachId){
			const staff = (this.team.staffMembers || []).find(s => s.id === participant.id ||
				(!!participant.coachId && s.coachId === participant.coachId));
			if (staff){
				return {
					firstName: staff.firstName,
					lastName: staff.lastName
				};
			}
		} else {
			// participant.id is the roster entry id, so it is the reliable key. athleteId is only a
			// fallback and must be non-null: null === null would match the first unlinked player.
			const player = this.team.players.find(p => p.id === participant.id ||
				(!!participant.athleteId && p.athleteId === participant.athleteId));
			if (player){
				return {
					firstName: player.firstName,
					lastName: player.lastName
				};
			}
		}
		// Fall back to the name stored on the event for anyone no longer on the roster
		const [firstName, ...lastName] = (participant.name || '').split(' ');
		return {
			firstName: firstName || participant.name,
			lastName: lastName.join(' ')
		};
	}
}
